<template>
  <v-card>
    <Subheader title="Approval Level Tenant Configurations" v-model="approvalSearch" search="Approval Levels">
      <v-btn color="add" @click="addApprovalLevel()">
        <v-icon class="mr-1">add</v-icon>
        {{ $t('Button.Add') }}
      </v-btn>
    </Subheader>
    <v-card-text>
      <v-alert outline color="info" :value="true" icon="info">
        An approval level configuration maps a database approval level with custom client-facing text. <code>Continuous Database Value</code> is the database approval level value targetted by this configuration. <code>Output Text</code> is the approval level text displayed to clients. <code>Output Description</code> is the explanation text for the value in the approval level legend at the bottom of a report.
      </v-alert>
      <v-data-table :headers="headers" :items="tenantApprovalLevelConfigurations" :search="approvalSearch" :pagination.sync="pagination" :loading="isLoading">
        <template slot="items" slot-scope="props">
          <td class="text-xs-left">{{props.item.MappedValue ? props.item.MappedValue : $t('ContinuousApprovalLevelManage.default')}}</td>
          <td class="text-xs-left">{{props.item.OutputText}}</td>
          <td class="text-xs-left">{{props.item.OutputDescription}}</td>
          <td class="text-xs-left">
            <EditButton small @click="updateApprovalLevel(props.item)">Edit</EditButton>
            <DeleteButton small @click="deleteApprovalLevel(props.item)" :loading="isBeingDeleted(props.item)">Delete</DeleteButton>
          </td>
        </template>
      </v-data-table>
    </v-card-text>
    <UpsertApprovalLevelForm v-model="upsertApprovalLevelFormModal" :approvalLevelConfiguration="approvalLevelConfigurationToUpdate" :tenantId="tenantId" />
    <ConfirmationDialog2 ref="confirmationDialog" />
  </v-card>
</template>
<script>
  import UpsertApprovalLevelForm from '@/components/ContinuousDataOutputManage/ContinuousApprovalLevels/Forms/UpsertApprovalLevelForm'
  import {
    createNamespacedHelpers,
  } from 'vuex'
  const app = createNamespacedHelpers('app')
  const approvalLevels = createNamespacedHelpers('approvalLevels')
  const authenticate = createNamespacedHelpers('authenticate')
  export default {
    components: {
      UpsertApprovalLevelForm,
    },
    props: {
      tenantName: {
        type: String,
        default: '',
      },
      tenantId: {
        type: Number,
      },
    },
    data() {
      return {
        approvalSearch: '',
        headers: [{
          text: 'Continuous Database Value',
          value: 'MappedValue',
          align: 'left',
        },
        {
          text: 'Output Text',
          value: 'OutputText',
          align: 'left',
        },
        {
          text: 'Output Description',
          value: 'OutputDescription',
          align: 'left',
        },
        {
          text: 'Actions',
          value: '',
          align: 'left',
          sortable: false,
        },
        ],
        pagination: {
          rowsPerPage: 10,
        },
        upsertApprovalLevelFormModal: false,
        approvalLevelConfigurationToUpdate: null,
        approvalLevelConfigurationToDelete: null,
      }
    },
    computed: {
      ...approvalLevels.mapState(['tenantApprovalLevelConfigurations', 'isLoading']),
      ...authenticate.mapGetters(['currentUserGroup']),
      currentTenantIsDifferentFromUserTenant() {
        return this.currentUserGroup.name != this.tenantName
      },
    },
    methods: {
      ...app.mapActions(['setSnackbar']),
      ...approvalLevels.mapMutations({ 'resetApprovalLevels': 'RESET' }),
      ...approvalLevels.mapActions(['getTenantApprovalLevelConfigurations', 'deleteTenantApprovalLevelConfiguration']),
      addApprovalLevel() {
        this.approvalLevelConfigurationToUpdate = null
        this.upsertApprovalLevelFormModal = true
      },
      updateApprovalLevel(approvalLevelConfiguration) {
        this.approvalLevelConfigurationToUpdate = approvalLevelConfiguration
        this.upsertApprovalLevelFormModal = true
      },
      async deleteApprovalLevel(approvalLevelConfiguration) {
        const isDefaultConfiguration = !approvalLevelConfiguration.MappedValue
        const params = {
          title: this.$t('ContinuousApprovalLevelManage.deleteTenantApprovalLevelConfirmation'),
          text: isDefaultConfiguration ?
            this.$t('ContinuousApprovalLevelManage.deleteTenantApprovalLevelTextDefault') : this.$t('ContinuousApprovalLevelManage.deleteTenantApprovalLevelTextNonDefault', { msg: approvalLevelConfiguration.MappedValue }),
          confirmText: this.$t('Button.Delete'),
          cancelText: this.$t('Button.Cancel'),
          color: 'error',
          icon: 'delete_forever',
        }
        if (await this.$refs.confirmationDialog.confirm(params)) {
          this.approvalLevelConfigurationToDelete = approvalLevelConfiguration
          try {
            await this.deleteTenantApprovalLevelConfiguration({ configuration: approvalLevelConfiguration })
            await this.getTenantApprovalLevelConfigurations({ tenantId: this.tenantId })
            this.setSnackbar({
              icon: 'done',
              color: 'success',
              text: this.$t('ContinuousApprovalLevelManage.deleteSuccessMessage'),
            })
          } catch (ex) {
            const exResponse = ex && ex.response && ex.response.data
            const msg = exResponse && (exResponse.ExceptionMessage || exResponse.Message)
            this.setSnackbar({
              icon: 'error',
              text: this.$t('ContinuousApprovalLevelManage.deleteErrorMessage', { msg }),
              color: 'error',
            })
          } finally {
            this.approvalLevelConfigurationToDelete = null
          }
        }
      },
      isBeingDeleted(approvalLevelConfiguration) {
        if (!this.approvalLevelConfigurationToDelete) {
          return false
        }
        return this.approvalLevelConfigurationToDelete.Id == approvalLevelConfiguration.Id
      },
    },
    async mounted() {
      await this.getTenantApprovalLevelConfigurations({ tenantId: this.tenantId })
    },
    beforeDestroy() {
      if (this.tenantName && this.currentTenantIsDifferentFromUserTenant) {
        this.resetApprovalLevels()
      }
    },
  }
</script>